/* eslint max-classes-per-file: 0 */

import {
  BaseApiParams,
  BaseApiResponse,
  BaseApiResponseCollection,
} from '@/typings/common'
import {
  createAuthHeaderInterceptor,
  create401ErrorInterceptor,
} from './interceptors'
import { BaseApi } from './BaseApi'
import { AxiosRequestConfig, Method } from 'axios'

export abstract class BaseApiAuth<
  T,
  TT = T,
  BP extends BaseApiParams = BaseApiParams,
  BR extends BaseApiResponse<T> = BaseApiResponse<T>,
  BRC extends BaseApiResponseCollection<TT> = BaseApiResponseCollection<TT>,
  BRN extends BaseApiResponse<never> = BaseApiResponse<never>
> extends BaseApi<T, TT, BP, BR, BRC, BRN> {
  public static onAuthHeader: (hash: string) => void;

  public static onUnauthorized: () => void;

  constructor(
    protected readonly baseURL = BaseApi.getDefaultBaseURL(),
    protected readonly endpointPrefix = process.env.VUE_APP_ADMIN_PREFIX,
  ) {
    super(baseURL, endpointPrefix)
  }

  protected setInterceptors() {
    // Обязательно ставим перед ExtractInterceptor
    this.http.interceptors.response.use(
      createAuthHeaderInterceptor(BaseApiAuth),
      create401ErrorInterceptor(BaseApiAuth),
    )

    super.setInterceptors()
  }
}

export abstract class BaseLkApiAuth<
  T,
  TT = T,
  BP extends BaseApiParams = BaseApiParams,
  BR extends BaseApiResponse<T> = BaseApiResponse<T>,
  BRC extends BaseApiResponseCollection<TT> = BaseApiResponseCollection<TT>,
  BRN extends BaseApiResponse<never> = BaseApiResponse<never>
> extends BaseApiAuth<T, TT, BP, BR, BRC, BRN> {
  constructor(
    protected readonly baseURL = BaseApi.getDefaultBaseURL(),
    protected readonly endpointPrefix = process.env.VUE_APP_LK_PREFIX,
  ) {
    super(baseURL, endpointPrefix)
  }
}

export abstract class BaseCollectorApiAuth<
  T,
  TT = T,
  BP extends BaseApiParams = BaseApiParams,
  BR extends BaseApiResponse<T> = BaseApiResponse<T>,
  BRC extends BaseApiResponseCollection<TT> = BaseApiResponseCollection<TT>,
  BRN extends BaseApiResponse<never> = BaseApiResponse<never>
> extends BaseApiAuth<T, TT, BP, BR, BRC, BRN> {
  constructor(
    protected readonly baseURL = process.env.VUE_APP_COLLECTOR_API_URL,
    protected readonly endpointPrefix = process.env
      .VUE_APP_COLLECTOR_API_URL_PREFIX,
  ) {
    super(baseURL, endpointPrefix)
    this.http.defaults.headers.common['X-Access-Token'] = process.env.VUE_APP_COLLECTOR_API_TOKEN
  }

  public request<T1, R = T1>(
    method: Method,
    url: string,
    config: AxiosRequestConfig = {},
  ): Promise<R> {
    return super.request<T1, R>(method, url, {
      withCredentials: true,
      ...config,
    })
  }
}

export abstract class BaseMarksApiAuth<
  T,
  TT = T,
  BP extends BaseApiParams = BaseApiParams,
  BR extends BaseApiResponse<T> = BaseApiResponse<T>,
  BRC extends BaseApiResponseCollection<TT> = BaseApiResponseCollection<TT>,
  BRN extends BaseApiResponse<never> = BaseApiResponse<never>
> extends BaseApiAuth<T, TT, BP, BR, BRC, BRN> {
  constructor(
    protected readonly baseURL = BaseApi.getDefaultBaseURL(),
    protected readonly endpointPrefix = process.env
      .VUE_APP_MARKS_API_URL_PREFIX,
  ) {
    super(baseURL, endpointPrefix)
  }

  public parse(p: BP, config?: AxiosRequestConfig) {
    const path = 'parse'
    const [url] = this.getUrl(path, p)
    const data = new FormData()
    // eslint-disable-next-line no-restricted-syntax
    for (const [key, val] of Object.entries(p)) {
      if (['string', 'boolean'].includes(typeof val)) {
        data.append(key, val as string)
      } else if (!!val && typeof val === 'object') {
        data.append(key, val as Blob)
      }
    }
    return this.request<never, BaseApiResponse<T, TT>>(
      'POST',
      url,
      { data, ...config },
    )
  }
}
